import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import {
    PostProductPayload,
    PatchProductPayload,
    Product
} from 'services/products/types';
import { setColumnWidth } from 'services/columnWidths/actions';
import {
    createProduct,
    requestedUpdateProduct,
    requestedDeleteProduct
} from 'services/products/actions';

import VirtualizedTable from 'components/VirtualizedTable';

import {
    selectProductsTableColumns,
    makeSelectProductItems,
    selectIsWritable
} from './selectors';

interface OwnProps {
    tableId: string;
}

interface ConnectedProps {
    items: VirtualizedTableItem[];
    columns: VirtualizedTableColumns;
    isWritable?: ReturnType<typeof selectIsWritable>;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    createProduct: typeof createProduct;
    requestedUpdateProduct: typeof requestedUpdateProduct;
    requestedDeleteProduct: typeof requestedDeleteProduct;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class ProductsVirtualizedTable extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleCreate = (item: VirtualizedTableItem): void => {
        this.props.createProduct(item as PostProductPayload);
    };

    handleDelete = (items: VirtualizedTableItem[]): void => {
        if (items[0] && items[0].id) {
            this.props.requestedDeleteProduct(items[0].id);
        }
    };

    handleItemUpdate = (item: VirtualizedTableItem): void => {
        const {
            id,
            unitTypeId,
            witholdingPeriodDays,
            reEntryPeriodDays,
            primaryTargetName,
            secondaryTargetName,
            activeIngredientIds,
            cropSureId,
            minTreeAgeMonths
        } = item as Product;

        this.props.requestedUpdateProduct(id, {
            unitTypeId,
            witholdingPeriodDays,
            reEntryPeriodDays,
            primaryTargetName,
            secondaryTargetName,
            activeIngredientIds,
            cropSureId,
            minTreeAgeMonths
        } as PatchProductPayload);
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const { tableId, items, columns, isWritable } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                label="Product"
                labelPlural="Products"
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                enableCreating={isWritable}
                enableDeleting={isWritable}
                selectable={isWritable}
                onCreate={this.handleCreate}
                onDelete={this.handleDelete}
                onItemUpdate={this.handleItemUpdate}
                editable={isWritable}
                enableItemEditing={isWritable}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    items: makeSelectProductItems(),
    columns: selectProductsTableColumns,
    isWritable: selectIsWritable
});

export default connect(mapStateToProps, {
    setColumnWidth,
    createProduct,
    requestedUpdateProduct,
    requestedDeleteProduct
})(ProductsVirtualizedTable);
